import type { GetServerSideProps } from 'next';

import { getLocaleFromParams } from '@/utils/locale';
import { extractPathFromUrl } from '@/utils/urlUtils';

import { getTranslatedStoreValue } from '@/components/StoresList/utils';
import { fetchStoreBySlugSSR } from '@/integrations/algolia/store';
import {
  BoutiqueDetailCMSTypeEnum,
  fetchCMSBoutiqueDetailContentWithExtraData,
} from '@/lib/boutique/helpers';
import { commonServerSideProps } from '@/lib/commonPageStaticProps';
import { BASE_STORE_FINDER_URL } from '@/lib/const';
import { resolveContentDraftMode } from '@/lib/draftMode';
import { capture404Event } from '@/lib/errors';
import { BOUTIQUE_DETAIL_PAGE_ERRORS } from '@/lib/notFoundErrors';
import {
  getContentfulRegionFromLocale,
  getRegionFromLocale,
} from '@/lib/regions';
import { CacheHeaderType } from '@/lib/revalidate';
import { BoutiqueDetailViewWrapper } from '@/views/BoutiqueDetailView';

const LANG_MAP = {
  'en-US': 'en',
  'en-GB': 'en',
};

export const getServerSideProps: GetServerSideProps = commonServerSideProps(
  async (context) => {
    const draftMode = resolveContentDraftMode(context);
    const routerLocale = getLocaleFromParams(context?.params);
    const { country, locale } = getRegionFromLocale(routerLocale);
    const storeSlug = context.params?.boutiqueslug as string;
    const { countryCode } = getContentfulRegionFromLocale(routerLocale);
    const contentfulLocale = locale.contentful ?? locale.locale;
    try {
      const store = await fetchStoreBySlugSSR(
        routerLocale,
        storeSlug,
        draftMode,
      );

      if (!store) {
        capture404Event('Boutique detail page', {
          requestUrl: context.req.url
            ? extractPathFromUrl(context.req.url)
            : '',
          reason:
            BOUTIQUE_DETAIL_PAGE_ERRORS.BOUTIQUE_DETAIL_PAGE_STORE_ID_NOT_FOUND,
        });
        return {
          notFound: true,
        };
      }

      const { cmsExtraData, items, seoConfigBuilder } =
        await fetchCMSBoutiqueDetailContentWithExtraData(
          store.store_id,
          countryCode,
          locale.code,
          country.channelSlug,
          contentfulLocale,
          BoutiqueDetailCMSTypeEnum.Detail,
        );
      const newSeoVariables = {
        boutiqueName: getTranslatedStoreValue(store, 'name', locale.slug) || '',
      };
      seoConfigBuilder.mergeSeoVariables(newSeoVariables);
      if (!items || items.length === 0) {
        return {
          notFound: true,
        };
      } else {
        const lang: string =
          LANG_MAP[locale.locale as keyof typeof LANG_MAP] ??
          locale.locale ??
          'en';

        if (!['FLAG_SHIP_BOUTIQUE', 'BOUTIQUE']?.includes(store.type)) {
          return {
            redirect: {
              destination: `${BASE_STORE_FINDER_URL}${lang.toLowerCase()}?storeId=${
                store.store_id
              }`,
              permanent: false,
            },
          };
        }

        return {
          props: {
            cmsBoutiqueDetailItems: items,
            cmsExtraData,
            store: store,
            seoConfig: seoConfigBuilder?.build(),
            draftMode: !!draftMode,
          },
        };
      }
    } catch (error) {
      capture404Event('Boutique detail page', {
        requestUrl: context.req.url ? extractPathFromUrl(context.req.url) : '',
        reason:
          BOUTIQUE_DETAIL_PAGE_ERRORS.BOUTIQUE_DETAIL_PAGE_RETRIEVE_STORE_ID_ERROR,
      });
      return {
        notFound: true,
      };
    }
  },
  CacheHeaderType.OTHER,
);
export default BoutiqueDetailViewWrapper;
